import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Tag } from "@/components/Layout";
import { projectStatusTone } from "@/lib/statusTone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import GanttChart from "@/components/GanttChart";
import { ChevronLeft, List, BarChart3, Plus, Package2, Trash2, Upload, Pencil, Eye, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import WorkingDayPicker from "@/components/WorkingDayPicker";
import ProjectUpdatesFeed from "@/components/ProjectUpdatesFeed";
import InternalUpdatesFeed from "@/components/InternalUpdatesFeed";
import ProjectChatSidecar from "@/components/ProjectChatSidecar";
import ProjectLinks from "@/components/ProjectLinks";
import ProjectPriorities from "@/components/ProjectPriorities";
import { formatDate } from "@/lib/dateFormat";
import HardwareTabs from "@/components/HardwareTabs";
import ExternalCollaborators from "@/components/ExternalCollaborators";
import GlobalInvoiceWizard from "@/components/GlobalInvoiceWizard";
import ProjectInvoicesSection from "@/components/ProjectInvoicesSection";
import ProjectAddActions from "@/components/ProjectAddActions";
import ProjectDailyTasksSection from "@/components/ProjectDailyTasksSection";
import ProjectClientRequestsSection from "@/components/ProjectClientRequestsSection";
import MarketingProjectView from "@/components/MarketingProjectView";
import ProjectWeeklyReportSection from "@/components/ProjectWeeklyReportSection";
import ProjectPnLSection from "@/components/ProjectPnLSection";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";
import usePermissions from "@/lib/usePermissions";
import { toast } from "sonner";

function weeksLeft(endDateIso) {
  if (!endDateIso) return null;
  const end = new Date(endDateIso);
  if (isNaN(end)) return null;
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return { value: 0, label: "Past due", tone: "text-red-600" };
  const weeks = Math.ceil(ms / (1000 * 60 * 60 * 24 * 7));
  if (weeks <= 2) return { value: weeks, label: `${weeks} week${weeks === 1 ? "" : "s"} left`, tone: "text-[#F54800]" };
  if (weeks <= 6) return { value: weeks, label: `${weeks} weeks left`, tone: "text-amber-600" };
  return { value: weeks, label: `${weeks} weeks left`, tone: "text-slate-600" };
}

function milestonesDone(milestones) {
  const list = milestones || [];
  const done = list.filter((m) => m.status === "done").length;
  return { done, total: list.length, pending: list.length - done };
}

const STATUS_OPTIONS = [
  { v: "planned", label: "Planned", tone: "slate" },
  { v: "in_progress", label: "In progress", tone: "blue" },
  { v: "blocked", label: "Blocked", tone: "orange" },
  { v: "done", label: "Done", tone: "green" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const { user, adminMode } = useAuth();
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [form, setForm] = useState({ title: "", description: "", due_date: "", status: "planned", order: 0 });
  const [editMs, setEditMs] = useState(null);
  const [deliverablesVersion, setDeliverablesVersion] = useState(0); // Phase 115 — bumped on deliverable add/delete so ContractFulfillmentSection re-fetches

  const load = useCallback(async () => {
    const [p, m] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/milestones`),
    ]);
    setProject(p.data);
    setMilestones(m.data);
    // Project team — used to populate the Priorities assignee dropdowns.
    try {
      const ids = p.data?.team_ids || [];
      if (ids.length > 0) {
        const { data: users } = await api.get("/users", { params: { ids: ids.join(",") } });
        setMembers((users || []).filter((u) => ids.includes(u.id)));
      } else {
        setMembers([]);
      }
    } catch {
      setMembers([]);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const addMilestone = async () => {
    try {
      await api.post("/milestones", { ...form, project_id: id, order: Number(form.order) || milestones.length });
      toast.success("Milestone added");
      setOpen(false);
      setForm({ title: "", description: "", due_date: "", status: "planned", order: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateMilestone = async (mid, patch) => {
    try {
      await api.patch(`/milestones/${mid}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const can = usePermissions();
  if (!project) return <div className="p-8 text-slate-500">Loading…</div>;
  const canEdit = user && user.role !== "client";
  const canApproveMilestone = can("projects_edit_milestones") || can("projects_edit");
  const isAdmin = can("projects_edit");
  const effectiveAdmin = isAdmin && adminMode;  // honours the Admin Mode toggle
  const startEditMs = (m) => setEditMs({ ...m });
  const saveEditMs = async () => {
    if (!editMs?.title?.trim()) { toast.error("Title required"); return; }
    try {
      await api.patch(`/milestones/${editMs.id}`, {
        title: editMs.title, description: editMs.description || "", due_date: editMs.due_date || null, status: editMs.status,
      });
      toast.success("Milestone updated");
      setEditMs(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteMs = async (m) => {
    if (!window.confirm(`Delete milestone "${m.title}"?`)) return;
    try { await api.delete(`/milestones/${m.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fade-in" data-testid="project-detail">
      <ProjectChatSidecar projectId={id} />
      {/* Sticky compact header — always visible while scrolling long projects.
          Mobile: pin below the 56px fixed mobile-topbar. Desktop: below the 48px sticky topbar. */}
      <div className="sticky top-14 lg:top-12 z-10 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="p-3 sm:px-8 max-w-6xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1 flex items-center gap-3">
            <Link to="/projects" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900 shrink-0" data-testid="back-to-projects">
              <ChevronLeft size={14} /> Projects
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-lg sm:text-xl font-bold text-slate-900 truncate" data-testid="project-detail-title">{project.name}</h1>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[11px] text-slate-500 truncate max-w-[220px]">{project.client_name}</span>
                <Tag tone={projectStatusTone(project.status)}>{project.status.replace("_", " ")}</Tag>
                {project.service && (
                  <Tag tone="brand" data-testid="project-service-tag">
                    {project.service === "other" ? (project.service_other || "Other") : project.service.replace("_", " ")}
                  </Tag>
                )}
                {(() => {
                  const wl = weeksLeft(project.end_date);
                  return wl ? <span className={`text-[11px] font-semibold ${wl.tone}`}>{wl.label}</span> : null;
                })()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && <ProjectAddActions project={project} onCreated={load} />}
            <GlobalInvoiceWizard project={project} onSaved={load} />
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-8 max-w-6xl mx-auto">

      {/* Phase 115 — Hero image + Project Details now render immediately,
          always-visible (not collapsed inside an accordion) so the project
          overview is the first thing anyone sees. Marketing-hub projects
          keep their own bespoke overview, same bypass as before. */}
      {!project.marketing_hub && (
        <ProjectOverviewCard project={project} milestones={milestones} canSeePnl={can("finance") || (user && user.role === "client_manager" && project.client_manager_id === user.id)} />
      )}

      {/* Priorities This Week — Dashboard-parity widget with week toggle + max 3 open. */}
      <div className="mt-6">
        <ProjectPriorities project={project} milestones={milestones} members={members} canEdit={canEdit} onChanged={load} />
      </div>

      {/* Contract Fulfillment — SOW auto-audit, sits with the other
          front-and-center project details, above the collapsible sections. */}
      {canEdit && !project.marketing_hub && (
        <div className="mt-6">
          <ContractFulfillmentSection projectId={id} project={project} refreshKey={deliverablesVersion} />
        </div>
      )}

      {/* Everything below is wrapped in collapsible accordions so long projects stay scannable. */}
      <ProjectSectionsAccordion
        project={project}
        milestones={milestones}
        members={members}
        canEdit={canEdit}
        isAdmin={isAdmin}
        canSeePnl={can("finance") || (user && user.role === "client_manager" && project.client_manager_id === user.id)}
        canApproveMilestone={canApproveMilestone}
        effectiveAdmin={effectiveAdmin}
        view={view}
        setView={setView}
        open={open}
        setOpen={setOpen}
        form={form}
        setForm={setForm}
        STATUS_OPTIONS={STATUS_OPTIONS}
        addMilestone={addMilestone}
        updateMilestone={updateMilestone}
        startEditMs={startEditMs}
        deleteMs={deleteMs}
        id={id}
        load={load}
        setDeliverablesVersion={setDeliverablesVersion}
      />

      {/* Comments / Chat — now sits below the project details + milestones,
          so the overview is always front-and-center on page load. */}
      <div className="mt-6">
        <ProjectUpdatesFeed projectId={id} />
      </div>

      <Dialog open={!!editMs} onOpenChange={(v) => !v && setEditMs(null)}>
        <DialogContent className="rounded-sm" data-testid="edit-ms-dialog">
          {editMs && (
            <>
              <DialogHeader><DialogTitle className="font-display">Edit milestone</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" value={editMs.title} onChange={(e) => setEditMs({ ...editMs, title: e.target.value })} data-testid="edit-ms-title" /></div>
                <div><Label className="label-overline">Description</Label><Textarea rows={3} className="rounded-sm mt-1" value={editMs.description || ""} onChange={(e) => setEditMs({ ...editMs, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="label-overline">Due date</Label><Input type="date" className="rounded-sm mt-1" value={editMs.due_date || ""} onChange={(e) => setEditMs({ ...editMs, due_date: e.target.value })} /></div>
                  <div><Label className="label-overline">Status</Label>
                    <Select value={editMs.status} onValueChange={(v) => setEditMs({ ...editMs, status: v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditMs(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={saveEditMs} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="save-edit-ms">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Collapsible sections wrapper. Uses Shadcn Accordion (type="multiple") so
 * users can pop several open at once. Open state is persisted per-user in
 * localStorage so navigating away + back doesn't blow away their layout.
 * -------------------------------------------------------------------------- */
const DEFAULT_OPEN_SECTIONS = ["kanban", "requests"];
const STORAGE_KEY = "projectDetail.openSections";

function ProjectSectionsAccordion({
  project, milestones, members, canEdit, isAdmin, canSeePnl, canApproveMilestone, effectiveAdmin,
  view, setView, open, setOpen, form, setForm, STATUS_OPTIONS,
  addMilestone, updateMilestone, startEditMs, deleteMs, id, load, setDeliverablesVersion,
}) {
  const can = usePermissions();
  const [openSections, setOpenSections] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return DEFAULT_OPEN_SECTIONS;
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(openSections)); } catch { /* ignore */ }
  }, [openSections]);

  // Marketing project keeps its bespoke hub — don't wrap it in accordions.
  if (project.marketing_hub) {
    return (
      <div className="mt-6" data-testid="project-marketing-hub">
        <MarketingProjectView projectId={project.id} />
      </div>
    );
  }

  return (
    <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="mt-6 space-y-2" data-testid="project-sections-accordion">
      {/* Workspace Links (5 customizable) */}
      <AccordionSection value="links" label="Workspace links" testId="section-links">
        <ProjectLinks project={project} canEdit={canEdit} canEditLink={effectiveAdmin} onUpdated={load} />
      </AccordionSection>

      {/* Phase 84.63 — gated Kanban "Board": every project gets one, always
          visible (independent of daily_task_sync, which only controls
          whether this project's tasks also surface in the Dashboard's
          global Daily Tasks widget). */}
      {canEdit && (
        <AccordionSection value="kanban" label="Board" testId="section-kanban">
          {isAdmin && (
            <div className="card-flat px-4 py-3 mb-3 flex items-center justify-between gap-3" data-testid="project-daily-task-sync-card">
              <div>
                <div className="text-sm font-semibold text-slate-900">Also show on Dashboard's Daily Tasks widget</div>
                <div className="text-[11px] text-slate-500">When ON, this project's Board tasks also show up in the global dashboard widget so the team can attach quick tasks + log time directly against it.</div>
              </div>
              <label className="inline-flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!project.daily_task_sync}
                  onChange={async (e) => {
                    try {
                      await api.patch(`/projects/${id}`, { daily_task_sync: e.target.checked });
                      toast.success(`Daily-task sync ${e.target.checked ? "enabled" : "disabled"}`);
                      load();
                    } catch (err) { toast.error(formatApiError(err)); }
                  }}
                  data-testid="project-daily-task-sync-toggle"
                  className="accent-[#F54800]"
                />
                <span className="text-slate-700">{project.daily_task_sync ? "On" : "Off"}</span>
              </label>
            </div>
          )}
          <ProjectDailyTasksSection projectId={id} project={project} />
        </AccordionSection>
      )}

      {/* Client requests inbox */}
      {canEdit && (
        <AccordionSection value="requests" label="Client requests" testId="section-requests">
          <ProjectClientRequestsSection projectId={id} />
        </AccordionSection>
      )}

      {/* Weekly Report — Feb 2026 revamp. Moved out of the old My Work >
          Weekly Reports hub tab into a dedicated per-project section anchored
          to this project's milestones + SOW scope. */}
      {canEdit && project.client_user_id && (can("weekly_reports") || can("my_work_weekly_reports")) && (
        <AccordionSection value="weekly-report" label="Weekly report" testId="section-weekly-report">
          <ProjectWeeklyReportSection project={project} canEdit={canEdit} />
        </AccordionSection>
      )}

      {/* Project P&L — Studio ERP (Aug 2026). Founder / Admin / this project's Client Manager. */}
      {canSeePnl && (
        <AccordionSection value="pnl" label="Project P&L" testId="section-pnl">
          <ProjectPnLSection projectId={id} />
        </AccordionSection>
      )}

      {/* External collaborators (task-driven) */}
      {project.template === "task_driven" && canEdit && (
        <AccordionSection value="collaborators" label="External collaborators" testId="section-collaborators">
          <ExternalCollaborators project={project} canEdit={isAdmin} onUpdated={load} />
        </AccordionSection>
      )}

      {/* Internal updates */}
      {canEdit && (
        <AccordionSection value="internal-updates" label="Internal updates" testId="section-internal-updates">
          <InternalUpdatesFeed />
        </AccordionSection>
      )}

      {/* Hardware (physical product) */}
      {project.physical_product && (
        <AccordionSection value="hardware" label="Hardware" testId="section-hardware">
          <HardwareTabs projectId={id} canEdit={canEdit} />
        </AccordionSection>
      )}

      {/* Milestones */}
      <AccordionSection value="milestones" label={`Milestones${milestones.length ? ` (${milestones.filter((m) => m.status === "done").length}/${milestones.length})` : ""}`} testId="section-milestones">
        <MilestonesSection
          view={view}
          setView={setView}
          open={open}
          setOpen={setOpen}
          form={form}
          setForm={setForm}
          STATUS_OPTIONS={STATUS_OPTIONS}
          addMilestone={addMilestone}
          updateMilestone={updateMilestone}
          startEditMs={startEditMs}
          deleteMs={deleteMs}
          project={project}
          milestones={milestones}
          canEdit={canEdit}
          isAdmin={isAdmin}
          canApproveMilestone={canApproveMilestone}
        />
      </AccordionSection>

      {/* Deliverables */}
      {canEdit && (
        <AccordionSection value="deliverables" label="Deliverables" testId="section-deliverables">
          <DeliverablesSection projectId={id} project={project} onChanged={() => setDeliverablesVersion((v) => v + 1)} />
        </AccordionSection>
      )}

      {/* Invoices */}
      {isAdmin && (
        <AccordionSection value="invoices" label="Invoices" testId="section-invoices">
          <ProjectInvoicesSection projectId={id} projectName={project.name} />
        </AccordionSection>
      )}
    </Accordion>
  );
}

function AccordionSection({ value, label, children, testId }) {
  return (
    <AccordionItem value={value} className="card-flat border-slate-200 rounded-sm overflow-hidden" data-testid={testId}>
      <AccordionTrigger className="px-5 py-3 text-sm font-semibold text-slate-900 hover:no-underline hover:bg-slate-50" data-testid={`${testId}-trigger`}>
        {label}
      </AccordionTrigger>
      <AccordionContent className="px-5 pt-1 pb-4 border-t border-slate-100" data-testid={`${testId}-content`}>
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

function ProjectOverviewCard({ project, milestones, canSeePnl }) {
  const [pnl, setPnl] = useState(null);
  useEffect(() => {
    if (!canSeePnl) return;
    api.get(`/projects/${project.id}/pnl`).then((r) => setPnl(r.data)).catch(() => {});
  }, [project.id, canSeePnl]);
  const margin = pnl ? pnl.revenue - pnl.direct_costs : null;
  const marginPct = pnl && pnl.revenue > 0 ? Math.round((margin / pnl.revenue) * 1000) / 10 : null;

  return (
    <div className="card-flat overflow-hidden" data-testid="project-overview-card">
      {project.cover_image && (
        <div className="aspect-[3/1] bg-slate-100 overflow-hidden">
          <img src={project.cover_image} alt={project.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Tag tone="slate">{project.business_unit}</Tag>
          {project.category && <Tag tone="orange" data-testid="project-category-tag">{project.category.replace(/_/g, " ")}</Tag>}
          {project.template === "task_driven" && (
            <Tag tone="blue" data-testid="project-template-tag">Task-driven</Tag>
          )}
        </div>
        {project.description && (
          <p className="text-slate-700 max-w-3xl break-words whitespace-pre-wrap">{project.description}</p>
        )}
        {(project.goals?.length || project.problems?.length || project.outcomes?.length) ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 pt-5 border-t border-slate-100" data-testid="project-scope">
            {[
              { label: "Goals", items: project.goals, tone: "text-green-700" },
              { label: "Problems to solve", items: project.problems, tone: "text-orange-700" },
              { label: "Expected outcomes", items: project.outcomes, tone: "text-[#F54800]" },
            ].map((col) => (
              <div key={col.label}>
                <div className={`label-overline mb-1.5 ${col.tone}`}>{col.label}</div>
                {col.items?.length ? (
                  <ul className="space-y-1 text-sm text-slate-700">
                    {col.items.map((it, i) => <li key={`${i}:${it}`} className="flex gap-1.5 break-words"><span className="text-slate-400">•</span><span>{it}</span></li>)}
                  </ul>
                ) : <div className="text-xs text-slate-400 italic">Not specified</div>}
              </div>
            ))}
          </div>
        ) : null}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div><div className="label-overline">Client</div><div className="text-sm mt-1 truncate" data-testid="project-client-name">{project.client_name || "—"}</div></div>
          <div><div className="label-overline">Start</div><div className="text-sm mt-1">{formatDate(project.start_date)}</div></div>
          <div>
            <div className="label-overline">End</div>
            <div className="text-sm mt-1">{formatDate(project.end_date)}</div>
            {(() => {
              const wl = weeksLeft(project.end_date);
              const ms = milestonesDone(milestones);
              if (!wl && !ms.total) return null;
              return (
                <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid="project-countdown">
                  {wl && <span className={`text-[11px] font-semibold ${wl.tone}`}>{wl.label}</span>}
                  {ms.total > 0 && <span className="text-[11px] text-slate-500">· {ms.done}/{ms.total} milestones done</span>}
                  {(project.tasks_total ?? 0) > 0 && (
                    <span className="text-[11px] text-slate-400" data-testid="project-tasks-count">
                      · {project.tasks_done ?? 0}/{project.tasks_total} tasks completed
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
          <div><div className="label-overline">Budget</div><div className="text-sm mt-1">₹ {Number(project.budget || 0).toLocaleString("en-IN")}</div></div>
          <div><div className="label-overline">Team</div><div className="text-sm mt-1">{(project.team_ids || []).length} members</div></div>
          {canSeePnl && margin !== null && (
            <div>
              <div className="label-overline">Margin</div>
              <div className={`text-sm mt-1 font-semibold ${margin >= 0 ? "text-green-700" : "text-red-600"}`} data-testid="project-margin">
                ₹ {Math.abs(margin).toLocaleString("en-IN")} {margin < 0 && "(loss)"} {marginPct !== null && `· ${marginPct}%`}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContractFulfillmentSection({ projectId, project, refreshKey }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/projects/${projectId}/contract-fulfillment`).then((r) => setData(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data || data.total === 0) return null; // no SOW scope points defined — nothing to audit

  const pct = Math.round((data.fulfilled_count / data.total) * 100);
  return (
    <div className="card-flat p-5" data-testid="contract-fulfillment-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-sm text-slate-900">Contract fulfillment</h3>
          <p className="text-xs text-slate-500">Auto-audit against the project's SOW goals, problems & outcomes.</p>
        </div>
        <div className={`text-sm font-bold ${data.complete ? "text-green-700" : "text-red-600"}`} data-testid="contract-fulfillment-pct">
          {data.fulfilled_count}/{data.total} · {pct}%
        </div>
      </div>
      <div className="space-y-1.5">
        {data.scope_points.map((sp, i) => (
          <div
            key={`${sp.source}-${i}`}
            className={`flex items-start gap-2 text-sm px-3 py-2 rounded-sm ${sp.fulfilled ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}
            data-testid={`sow-point-${sp.fulfilled ? "fulfilled" : "missing"}-${i}`}
          >
            {sp.fulfilled ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> : <AlertTriangle size={14} className="shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <span className="break-words">{sp.label}</span>
              <span className="text-[10px] uppercase tracking-wider ml-2 opacity-60">{sp.source}</span>
              {sp.fulfilled && sp.deliverable_title && <div className="text-[11px] opacity-70 mt-0.5">via "{sp.deliverable_title}"</div>}
            </div>
          </div>
        ))}
      </div>
      {!data.complete && (
        <p className="text-[11px] text-red-600 mt-3 inline-flex items-center gap-1" data-testid="contract-fulfillment-block-notice">
          <AlertTriangle size={11} />This project cannot be marked Completed until every scope point above has a Deliverable with a verified Drive link.
        </p>
      )}
    </div>
  );
}

function MilestonesSection({
  view, setView, open, setOpen, form, setForm, STATUS_OPTIONS,
  addMilestone, updateMilestone, startEditMs, deleteMs,
  project, milestones, canEdit, isAdmin, canApproveMilestone,
}) {
  return (
    <>
      <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
        <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden">
          <button onClick={() => setView("list")} className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 ${view === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="view-list"><List size={12} /> List</button>
          <button onClick={() => setView("gantt")} className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 ${view === "gantt" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="view-gantt"><BarChart3 size={12} /> Timeline</button>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-milestone-btn" className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"><Plus size={14} className="mr-1.5" />Add milestone</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-display">Add milestone</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label className="label-overline">Title</Label><Input data-testid="ms-title" className="rounded-sm mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label className="label-overline">Description</Label><Textarea className="rounded-sm mt-1" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="label-overline">Due date</Label><Input type="date" className="rounded-sm mt-1" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                  <div><Label className="label-overline">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={addMilestone} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white">Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {view === "gantt" ? (
        <div className="card-flat">
          <GanttChart project={project} milestones={milestones} />
        </div>
      ) : (
        <div className="card-flat divide-row">
          {milestones.map((m, i) => {
            const opt = STATUS_OPTIONS.find((s) => s.v === m.status) || STATUS_OPTIONS[0];
            return (
              <div key={m.id} className="p-4 flex items-center justify-between gap-4 hover-row" data-testid={`milestone-row-${m.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="font-mono text-xs text-slate-400 w-6">{String(i + 1).padStart(2, "0")}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 break-words">{m.title}</div>
                    <div className="text-xs text-slate-500">Due {formatDate(m.due_date)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Phase 84.62 — every milestone is a Launch Gate by default; it
                      only "closes" once BOTH approved=true AND status="done". */}
                  <Tag tone={m.approved && m.status === "done" ? "green" : "slate"} data-testid={`gate-status-${m.id}`}>
                    {m.approved && m.status === "done" ? "Gate closed" : "Gate open"}
                  </Tag>
                  {canApproveMilestone && (
                    <Button
                      size="sm"
                      variant="outline"
                      className={`rounded-sm h-8 text-xs ${m.approved ? "border-green-300 text-green-700 bg-green-50" : ""}`}
                      onClick={() => updateMilestone(m.id, { approved: !m.approved })}
                      data-testid={`approve-ms-${m.id}`}
                      title={m.approved ? "Revoke approval" : "Approve this milestone"}
                    >
                      {m.approved ? "✓ Approved" : "Approve"}
                    </Button>
                  )}
                  <Tag tone={opt.tone}>{opt.label}</Tag>
                  {canEdit && (
                    <Select value={m.status} onValueChange={(v) => updateMilestone(m.id, { status: v })}>
                      <SelectTrigger className="rounded-sm h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {isAdmin && (
                    <>
                      <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => startEditMs(m)} data-testid={`edit-ms-${m.id}`} title="Edit"><Pencil size={12} /></Button>
                      <Button size="sm" variant="outline" className="rounded-sm h-8 text-red-700 border-red-200 hover:bg-red-50" onClick={() => deleteMs(m)} data-testid={`del-ms-${m.id}`} title="Delete"><Trash2 size={12} /></Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {milestones.length === 0 && <div className="p-4 text-sm text-slate-500">No milestones yet.</div>}
        </div>
      )}
    </>
  );
}

function DeliverablesSection({ projectId, project, onChanged }) {
  const can = usePermissions();
  const canOverrideDate = can("projects_edit"); // Phase 137 — Founders/Admins only
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = { title: "", description: "", file_url: "", file_name: "", google_drive_link: "", scope_ref: "", delivered_at: "" };
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // deliverable row being edited, or null when adding
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { url, title }
  const fileInputRef = React.useRef(null);

  const scopeOptions = [
    ...(project?.goals || []).map((t) => ({ text: t, source: "Goal" })),
    ...(project?.problems || []).map((t) => ({ text: t, source: "Problem" })),
    ...(project?.outcomes || []).map((t) => ({ text: t, source: "Outcome" })),
  ];

  const load = useCallback(() => {
    api.get("/deliverables", { params: { project_id: projectId } }).then((r) => setRows(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      title: d.title || "", description: d.description || "", file_url: d.file_url || "",
      file_name: d.file_name || "", google_drive_link: d.google_drive_link || "",
      scope_ref: d.scope_ref || "", delivered_at: d.delivered_at || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      if (editing) {
        const payload = { title: form.title, description: form.description, file_url: form.file_url, file_name: form.file_name, google_drive_link: form.google_drive_link, scope_ref: form.scope_ref || null };
        if (canOverrideDate && form.delivered_at) payload.delivered_at = form.delivered_at;
        await api.patch(`/deliverables/${editing.id}`, payload);
        toast.success("Deliverable updated");
      } else {
        const payload = { ...form, scope_ref: form.scope_ref || undefined, project_id: projectId, delivered_at: canOverrideDate ? (form.delivered_at || undefined) : undefined };
        await api.post("/deliverables", payload);
        toast.success("Deliverable added");
      }
      setForm(emptyForm);
      setEditing(null);
      setOpen(false);
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this deliverable?")) return;
    try { await api.delete(`/deliverables/${id}`); load(); onChanged && onChanged(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const handleFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, file_url: data.path, file_name: data.filename || f.name }));
      toast.success("File uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  // Show the delivered-date field whenever a link is present. Founders/
  // Admins get it always (new delivery OR a retroactive correction on an
  // already-Delivered row); everyone else just sees informational copy.
  const showDateField = form.google_drive_link.trim().length > 0;
  const willBecomeDelivered = showDateField && (!editing || editing.status !== "Delivered");

  return (
    <div className="mt-8" data-testid="deliverables-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><Package2 size={18} className="text-[#F54800]" /> Deliverables</h2>
        <Button size="sm" onClick={openAdd} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="add-deliverable-btn"><Plus size={14} className="mr-1" /> Add deliverable</Button>
      </div>
      <div className="card-flat divide-row">
        {rows.map((d) => (
          <div key={d.id} className="p-4 flex items-start justify-between gap-3 hover-row" data-testid={`deliverable-row-${d.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                {d.title}
                {d.status === "Delivered" ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 border border-green-200 rounded-sm px-1.5 py-0.5" data-testid={`deliverable-status-${d.id}`}>
                    <CheckCircle2 size={10} /> Delivered
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5" data-testid={`deliverable-status-${d.id}`}>
                    <Clock size={10} /> Pending
                  </span>
                )}
              </div>
              {d.description && <div className="text-xs text-slate-500 mt-0.5">{d.description}</div>}
              <div className="text-[11px] text-slate-500 mt-1">
                {d.status === "Delivered"
                  ? <>Delivered · <b className="text-green-700">{formatDate(d.delivered_at)}</b></>
                  : <span className="text-amber-700">Awaiting Google Drive link</span>}
                {d.file_name && <> · <span className="text-slate-700">{d.file_name}</span></>}
                {d.scope_ref && <> · <span className="text-[#F54800]">Fulfills: {d.scope_ref}</span></>}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer" className="text-xs text-[#F54800] hover:underline">Open</a>}
              {d.google_drive_link && (
                <button onClick={() => setPreview({ url: d.google_drive_link, title: d.title })} className="text-slate-400 hover:text-[#F54800]" title="Preview" data-testid={`view-deliverable-${d.id}`}>
                  <Eye size={14} />
                </button>
              )}
              <button onClick={() => openEdit(d)} className="text-slate-400 hover:text-[#F54800]" title="Edit" data-testid={`edit-deliverable-${d.id}`}><Pencil size={14} /></button>
              <button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`delete-deliverable-${d.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-4 text-sm text-slate-500">No deliverables yet. Add one now — it starts as Pending until a Google Drive link is saved.</div>}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="deliverable-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit deliverable" : "Add deliverable"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Final renders pack v2" data-testid="deliv-title" /></div>
            <div><Label className="label-overline">Description</Label><Textarea rows={3} className="rounded-sm mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="deliv-description" /></div>
            <div>
              <Label className="label-overline">Google Drive link</Label>
              <Input className="rounded-sm mt-1" value={form.google_drive_link} onChange={(e) => setForm({ ...form, google_drive_link: e.target.value })} placeholder="https://drive.google.com/… (leave blank to keep as Pending)" data-testid="deliv-drive-link" />
              <p className="text-[11px] text-slate-500 mt-1">
                {willBecomeDelivered
                  ? "Saving this link marks the deliverable Delivered."
                  : showDateField ? "Already Delivered." : "No link yet — this deliverable will be saved as Pending."}
              </p>
            </div>
            {scopeOptions.length > 0 && (
              <div>
                <Label className="label-overline">Fulfills SOW scope point (optional)</Label>
                <Select value={form.scope_ref || "__none"} onValueChange={(v) => setForm({ ...form, scope_ref: v === "__none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="deliv-scope-ref"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None —</SelectItem>
                    {scopeOptions.map((s, i) => <SelectItem key={i} value={s.text}>{s.source}: {s.text}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="label-overline">File (optional)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input ref={fileInputRef} type="file" onChange={handleFilePick} className="hidden" data-testid="deliv-file-input" />
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="deliv-upload-btn"><Upload size={14} className="mr-1.5" /> {uploading ? "Uploading…" : "Upload file"}</Button>
                {form.file_name && <span className="text-xs text-slate-600 truncate">{form.file_name}</span>}
              </div>
            </div>
            {showDateField && canOverrideDate && (
              <div>
                <Label className="label-overline">{willBecomeDelivered ? "Delivered on (Founder/Admin override — defaults to today)" : "Delivered on (Founder/Admin — edit for a historical correction)"}</Label>
                <div className="mt-1"><WorkingDayPicker value={form.delivered_at?.slice(0, 10) || ""} onChange={(v) => setForm({ ...form, delivered_at: v })} placeholder="Pick delivery date" allowWeekends testId="deliv-date" /></div>
              </div>
            )}
            {willBecomeDelivered && !canOverrideDate && (
              <p className="text-[11px] text-slate-500">Delivered date will be set to today automatically.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="deliv-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DriveLinkPreviewModal open={!!preview} url={preview?.url} title={preview?.title} onClose={() => setPreview(null)} />
    </div>
  );
}
